import React from "react";
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { History, RotateCcw, Trash2, Clock, MessageSquare } from 'lucide-react';
import { ProjectSnapshot } from '@/hooks/useProjectSnapshots';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface VersionHistoryDialogProps {
  snapshots: ProjectSnapshot[];
  isLoading: boolean;
  onRestore: (snapshot: ProjectSnapshot) => Promise<void>;
  onDelete: (snapshotId: string) => Promise<boolean>;
}

export function VersionHistoryDialog({ 
  snapshots, 
  isLoading, 
  onRestore,
  onDelete 
}: VersionHistoryDialogProps) {
  const [open, setOpen] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<ProjectSnapshot | null>(null);

  const handleRestore = async (snapshot: ProjectSnapshot) => {
    setRestoring(snapshot.id);
    try {
      await onRestore(snapshot);
      setOpen(false);
    } finally {
      setRestoring(null);
      setConfirmRestore(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2">
            <History className="h-4 w-4" />
            History
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              Version History
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="h-[60vh] pr-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : snapshots.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>No version history yet</p>
                <p className="text-sm mt-1">Snapshots are created after each successful build (e.g. After edit, Initial scaffold)</p>
              </div>
            ) : (
              <div className="space-y-3">
                {snapshots.map((snapshot, index) => (
                  <div 
                    key={snapshot.id}
                    className="p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
                          <Clock className="h-3 w-3" />
                          {formatDistanceToNow(new Date(snapshot.created_at), { addSuffix: true })}
                          {index === 0 && (
                            <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-medium">
                              Latest
                            </span>
                          )}
                        </div>
                        <div className="flex items-start gap-2">
                          <MessageSquare className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                          <p className="text-sm text-foreground line-clamp-2">
                            {snapshot.label ?? snapshot.prompt}
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          {snapshot.file_count ?? snapshot.files_snapshot?.length ?? 0} files
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          disabled={restoring === snapshot.id || index === 0}
                          onClick={() => setConfirmRestore(snapshot)}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => onDelete(snapshot.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmRestore} onOpenChange={() => setConfirmRestore(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore to this version?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace your current app with the version from{' '}
              <strong>{confirmRestore ? formatDistanceToNow(new Date(confirmRestore.created_at), { addSuffix: true }) : ''}</strong>
              {confirmRestore?.label && ` (${confirmRestore.label})`}. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmRestore && handleRestore(confirmRestore)}
              disabled={!!restoring}
            >
              {restoring ? 'Restoring...' : 'Restore'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
